import apiConfig from '../constants/apiConfig';
import axios from 'axios';
import {
  AREA,
  DISTRICT,
  SECTOR,
  SUBUNIT,
} from '../components/MapPage/CorporateViewTabs/AorsTab/utils/schema';
import type { AreaOfResponsibility } from '../types/aor';
import { selectGroupGuid, useAppSelector } from '../slices/commonSelectors';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const IMPORTS = [
  {
    fileKey: 'CG_AREAS.geojson',
    levelOrder: 1,
    levelName: AREA,
    nameField: 'areaname',
  },
  {
    fileKey: 'CG_DISTRICTS.geojson',
    levelOrder: 2,
    levelName: DISTRICT,
    nameField: 'cfrdistrictname',
    parentNameField: 'areaname',
    lookupField: 'districtname',
    descriptionField: 'zones',
  },
  {
    fileKey: 'CG_SECTORS.geojson',
    levelOrder: 3,
    levelName: SECTOR,
    nameField: 'sectorname',
    parentNameField: 'districtname',
    descriptionField: 'emergency_contact_number',
  },
  {
    fileKey: 'CG_SUBUNITS.geojson',
    levelOrder: 4,
    levelName: SUBUNIT,
    nameField: 'stationname',
    namePrefix: 'Boat Station',
    parentNameField: 'sectorname',
  },
] as const;

const createAorsFromGeoJSON = async (groupGuid: string, files: File[]) => {
  const formData = new FormData();
  files.forEach((file) => formData.append(file.name, file));
  formData.append('imports', JSON.stringify(IMPORTS));
  return await axios.post<AreaOfResponsibility[]>(
    apiConfig.apiGateway.createAorsFromGeojson(groupGuid),
    formData
  );
};

export const useCreateAorsFromGeoJSON = () => {
  const queryClient = useQueryClient();
  const groupGuid = useAppSelector(selectGroupGuid);
  return useMutation({
    mutationFn: (files: File[]) => {
      if (!groupGuid)
        throw new Error('groupGuid is required to create AORs from GeoJSON');
      return createAorsFromGeoJSON(groupGuid, files);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['aors', groupGuid] });
      void queryClient.invalidateQueries({
        queryKey: ['aorsByHierarchy', groupGuid],
      });
    },
  });
};
